// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery, useMutation } from '@apollo/client';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { DateValidationError } from '@mui/x-date-pickers';
import { clientLogger } from '@seedplanner/logger';
import {
  CROP_STATUS,
  DEFAULT_CROP_NOTES,
  PLANTING_STATE,
  PLANTING_STATUS,
  type PlantingStatusType,
  formatTime,
  utils,
} from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Decimal from 'decimal.js';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '../../lib/base';
import { DASHBOARD_OVERDUE_TASKS_QUERY, DASHBOARD_QUERY } from '../../queries/DashboardQueries';
import { SURPLUSES_BUFFERS_QUERY } from '../../queries/SurplusesQueries';
import type {
  CreatePlantingMutation,
  CreatePlantingMutationVariables,
  CropsForNewPlantingQuery,
  CropsForNewPlantingQueryVariables,
} from '../../types/__generated__/graphql';
import { formatDuration, getID, getNumericID, getTimeRanges, shortenWeightUnit, validateNumInput } from '../../utils';
import { formatNumber } from '../../utils/formatNumber';
import { ExitDialogButton } from '../common/button';
import DialogInfoTitle from '../common/dailog/DailogInfoTitle';
import InternalDatePicker from '../common/date/InternalDatePicker';
import { sortCropsByName } from '../crop/sortCropsByName';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const CREATE_PLANTING_MUTATION = gql`
  mutation CreatePlanting(
    $crop_id: Int!
    $order_id: Int
    $planting_time: Int!
    $planting_state: String!
    $planting_status: String!
    $planting_notes: String
    $trays: Int!
    $crop_steps_completed: [Int!]!
    $planting_time_is_start_time: Boolean!
  ) {
    createPlanting(
      input: {
        crop_id: $crop_id
        order_id: $order_id
        planting_time: $planting_time
        planting_state: $planting_state
        planting_status: $planting_status
        planting_notes: $planting_notes
        trays: $trays
        crop_steps_completed: $crop_steps_completed
        planting_time_is_start_time: $planting_time_is_start_time
      }
    ) {
      planting_created {
        id
      }
      tasks_created {
        id
      }
      order_updated {
        id
        updated_at
      }
    }
  }
`;
const CROPS_AND_ORDERS_QUERY = gql`
  query CropsForNewPlanting {
    crops(filters: [{ name: "crop.status", operator: "!=", value: "archived" }]) {
      edges {
        node {
          id
          crop_name
          status
          total_grow_time
          yield_per_tray
          yield_unit
          buffer_percentage
          sowing_density
          sow_density_unit
          notes
          steps {
            edges {
              node {
                id
                name
                time_to_start_after_first_step
                description
              }
            }
          }
        }
      }
    }
  }
`;

interface Props {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: (shouldRefetchSelf?: boolean) => void;
  readonly orderInfo?: {
    readonly orderId: number;
    readonly orderFulfillmentTime: number;
  };
}

type CropData = NonNullable<CropsForNewPlantingQuery>['crops']['edges'][number]['node'];
type CropStepData =
  NonNullable<CropsForNewPlantingQuery>['crops']['edges'][number]['node']['steps']['edges'][number]['node'];

const DEFAULT_PLANTING_TIME_IS_START_TIME_STORAGE_KEY = 'plantingTimeIsStartTime';

export default function AddPlantingDialog({ isOpen, onClose, onSuccess, orderInfo }: Props) {
  const { timezone, language } = useUserContext();
  const [createPlanting, { loading: createPlantingLoading }] = useMutation<
    CreatePlantingMutation,
    CreatePlantingMutationVariables
  >(CREATE_PLANTING_MUTATION);
  const [getCropData, { loading: cropDataLoading }] = useLazyQuery<
    CropsForNewPlantingQuery,
    CropsForNewPlantingQueryVariables
  >(CROPS_AND_ORDERS_QUERY, {
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });

  const isForOrder = orderInfo !== undefined;
  const orderDate = isForOrder ? dayjs.unix(orderInfo.orderFulfillmentTime).tz(timezone) : undefined;
  const formattedOrderDate = isForOrder ? formatTime(orderInfo.orderFulfillmentTime, { timezone }) : undefined;

  const [filteredCropsData, setFilteredCropsData] = React.useState<
    NonNullable<CropsForNewPlantingQuery>['crops']['edges'][number]['node'][]
  >([]);
  const cropDataMap = React.useMemo(() => {
    const map = new Map<number, CropData>();
    for (const crop of filteredCropsData) {
      map.set(getNumericID(crop.id), crop);
    }
    return map;
  }, [filteredCropsData]);
  const filterAndSortCropsData = React.useCallback((cropDataIn: NonNullable<CropsForNewPlantingQuery>['crops']) => {
    const sortedCropData = cropDataIn.edges
      .map(({ node }) => node)
      .filter((crop) => crop.status !== CROP_STATUS.ARCHIVED);
    return sortCropsByName(sortedCropData);
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      void getCropData({
        onCompleted: (cropDataIn) => {
          if (cropDataIn?.crops != null) {
            const readyFilteredCropsData = filterAndSortCropsData(cropDataIn.crops);
            setFilteredCropsData(readyFilteredCropsData);
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(`Problem getting crop data: ${errorIn.message}`, {
            variant: 'error',
            autoHideDuration: 10000,
          });
        },
      });
    }
  }, [isOpen, getCropData, filterAndSortCropsData]);

  const disableFields = React.useMemo(
    () => createPlantingLoading || cropDataLoading,
    [createPlantingLoading, cropDataLoading],
  );
  const { todayStart, sevenDaysFromTodayStart } = React.useMemo(() => getTimeRanges(timezone), [timezone]);

  const [notes, setNotes] = React.useState('');
  const [harvestTime, setHarvestTime] = React.useState<dayjs.Dayjs | null>(
    orderDate !== undefined ? orderDate : utils.todayStart(timezone),
  );
  const [cropIdToUse, setCropIdToUse] = React.useState<number | null>(null);
  const [plantingStatus, setPlantingStatus] = React.useState<PlantingStatusType>(PLANTING_STATUS.PLANNED);
  const [trays, setTrays] = React.useState('');
  const traysNumber = React.useMemo(() => Number.parseFloat(trays) || 0, [trays]);
  const [traysValidation, setTraysValidation] = React.useState('');
  const [plantingTimeIsStartTime, setPlantingTimeIsStartTime] = React.useState(() => {
    const maybeVal = localStorage.getItem(DEFAULT_PLANTING_TIME_IS_START_TIME_STORAGE_KEY);
    if (maybeVal === null) {
      return false;
    }

    return maybeVal === 'true';
  });

  const [harvestDateError, setHarvestDateError] = React.useState<DateValidationError | null>(null);

  const harvestDateErrorMessage = React.useMemo(() => {
    if (harvestDateError == null) {
      return '';
    }
    switch (harvestDateError) {
      case 'maxDate':
        return `Harvest date cannot be after order date. Order date is ${formattedOrderDate}.`;
      default:
        return '';
    }
  }, [harvestDateError, formattedOrderDate]);

  const [selectedCropSteps, setSelectedCropSteps] = React.useState<
    { readonly cropStep: CropStepData; readonly isCompleted: boolean; readonly taskTime: number }[]
  >([]);
  const onChangeStepCompleted = React.useCallback((e: React.ChangeEvent<HTMLInputElement>, stepId: number) => {
    const newValue = e.target.checked;
    setSelectedCropSteps((prevSelectedCropSteps) => {
      const newSelectedCropSteps = prevSelectedCropSteps.map((step) => {
        if (getNumericID(step.cropStep.id) === stepId) {
          return { ...step, isCompleted: newValue };
        }
        return step;
      });
      if (newSelectedCropSteps.every((step) => step.isCompleted)) {
        setPlantingStatus(PLANTING_STATUS.COMPLETED);
      }
      if (newSelectedCropSteps.every((step) => !step.isCompleted)) {
        setPlantingStatus(PLANTING_STATUS.PLANNED);
      }

      return newSelectedCropSteps;
    });
  }, []);
  const onChangeNotes = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNotes(e.target.value);
  }, []);

  // If harvest date is selected, then return the start date
  // If start date is selected, then return the harvest date
  // const selectedCropStartOrHarvestTime = React.useMemo(() => {
  //   if (harvestTime == null || !harvestTime.isValid()) {
  //     return undefined;
  //   }
  //   if (cropIdToUse == null) {
  //     return undefined;
  //   }
  //   const cropData = cropDataMap.get(cropIdToUse);
  //   if (cropData == null) {
  //     return undefined;
  //   }
  //   const harvestTimeOut = plantingTimeIsStartTime ? harvestTime.add(cropData.total_grow_time, 'second') : harvestTime;
  //   const startTimeOut = plantingTimeIsStartTime
  //     ? harvestTime
  //     : harvestTime.subtract(cropData.total_grow_time, 'second');

  //   return plantingTimeIsStartTime ? harvestTimeOut : startTimeOut;
  // }, [plantingTimeIsStartTime, harvestTime, cropDataMap, cropIdToUse]);
  const setCropStepsWithCropAndHarvest = React.useCallback(
    ({
      cropId,
      harvestTime,
      plantingTimeIsStartTime,
    }: { readonly cropId?: number; readonly harvestTime?: dayjs.Dayjs; readonly plantingTimeIsStartTime: boolean }) => {
      if (cropId == null || harvestTime == null || !harvestTime.isValid()) {
        setSelectedCropSteps([]);
        return;
      }
      const cropData = cropDataMap.get(cropId);
      if (cropData === undefined) {
        setSelectedCropSteps([]);
        return;
      }
      const cropStartTime = plantingTimeIsStartTime
        ? harvestTime
        : harvestTime.subtract(cropData.total_grow_time, 'second');
      const steps = cropData.steps.edges.map(({ node }) => node);
      // Make sure earlier tasks are first
      steps.sort((a, b) => a.time_to_start_after_first_step - b.time_to_start_after_first_step);
      const mappedSteps = steps.map((step, idx) => {
        const taskTime = cropStartTime.add(step.time_to_start_after_first_step, 'second').unix();
        const isPastDue = taskTime < todayStart;
        const isLast = idx === steps.length - 1;
        // Make sure that at least the first one is marked completed if in progress
        const isFirst = idx === 0;

        return {
          cropStep: step,
          isCompleted:
            plantingStatus === PLANTING_STATUS.COMPLETED ||
            (plantingStatus === PLANTING_STATUS.IN_PROGRESS && (isPastDue || isFirst) && !isLast),
          taskTime,
        };
      });

      setSelectedCropSteps(mappedSteps);
    },
    [cropDataMap, plantingStatus, todayStart],
  );
  const onChangeHarvestTime = React.useCallback(
    (newValue: dayjs.Dayjs | null) => {
      setHarvestTime(newValue);
      setCropStepsWithCropAndHarvest({
        cropId: cropIdToUse ?? undefined,
        harvestTime: newValue ?? undefined,
        plantingTimeIsStartTime,
      });
    },
    [setCropStepsWithCropAndHarvest, cropIdToUse, plantingTimeIsStartTime],
  );
  const onChangePlantingTimeIsStartTime = React.useCallback(
    (_e: React.MouseEvent<HTMLElement>, newValue: boolean | null) => {
      if (newValue == null) {
        return;
      }
      setPlantingTimeIsStartTime(newValue);
      setCropStepsWithCropAndHarvest({
        cropId: cropIdToUse ?? undefined,
        harvestTime: harvestTime ?? undefined,
        plantingTimeIsStartTime: newValue,
      });
      localStorage.setItem(DEFAULT_PLANTING_TIME_IS_START_TIME_STORAGE_KEY, newValue.toString());
    },
    [setCropStepsWithCropAndHarvest, cropIdToUse, harvestTime],
  );
  const onChangeCropId = React.useCallback(
    (e: SelectChangeEvent<number>) => {
      const val = e.target.value;
      const newVal = typeof val === 'string' ? Number.parseInt(val) || null : val;
      setCropIdToUse(newVal);
      setCropStepsWithCropAndHarvest({
        cropId: newVal ?? undefined,
        harvestTime: harvestTime ?? undefined,
        plantingTimeIsStartTime,
      });
      if (notes === '' && newVal != null) {
        const maybeNotes = cropDataMap.get(newVal)?.notes;
        if (maybeNotes !== DEFAULT_CROP_NOTES) {
          setNotes(maybeNotes ?? '');
        }
      }
    },
    [harvestTime, setCropStepsWithCropAndHarvest, plantingTimeIsStartTime, cropDataMap, notes],
  );
  const weightFromTrays = React.useMemo(() => {
    if (cropIdToUse == null) {
      return undefined;
    }
    const crop = cropDataMap.get(cropIdToUse);
    if (crop === undefined) {
      return undefined;
    }

    return {
      weight: Decimal.mul(crop.yield_per_tray, traysNumber).toFixed(1),
      unit: crop.yield_unit,
      yield: crop.yield_per_tray,
    };
  }, [traysNumber, cropDataMap, cropIdToUse]);
  const seedForTrays = React.useMemo(() => {
    if (cropIdToUse == null) {
      return undefined;
    }
    const crop = cropDataMap.get(cropIdToUse);
    if (crop === undefined) {
      return undefined;
    }

    const seedNeeded =
      crop.sowing_density != null
        ? utils.round(Decimal.mul(crop.sowing_density, traysNumber).toNumber(), 0)
        : undefined;
    const seedPerTray = crop.sowing_density ?? undefined;
    const seedUnit = crop.sow_density_unit;
    const shortenedSeedUnit = shortenWeightUnit(seedUnit);

    return {
      seedNeeded,
      seedUnit,
      seedPerTray,
      shortenedSeedUnit,
    };
  }, [traysNumber, cropDataMap, cropIdToUse]);

  const onChangePlantingStatus = React.useCallback(
    (e: SelectChangeEvent<string>) => {
      setPlantingStatus(e.target.value as PlantingStatusType);
      if (e.target.value === PLANTING_STATUS.COMPLETED) {
        setSelectedCropSteps((prevSelectedCropSteps) =>
          prevSelectedCropSteps.map((step) => ({
            ...step,
            isCompleted: true,
          })),
        );
      } else if (e.target.value === PLANTING_STATUS.PLANNED) {
        setSelectedCropSteps((prevSelectedCropSteps) =>
          prevSelectedCropSteps.map((step) => ({
            ...step,
            isCompleted: false,
          })),
        );
      } else if (e.target.value === PLANTING_STATUS.IN_PROGRESS) {
        setSelectedCropSteps((prevSelectedCropSteps) => {
          const newSelectedCropSteps = prevSelectedCropSteps.map((step, idx) => {
            // If the task date is in the past, then mark as complete by default
            const isPastDue = step.taskTime < todayStart;
            // Make sure that at least the first one is marked completed
            const isFirst = idx === 0;
            const isLast = idx === prevSelectedCropSteps.length - 1;
            // Always mark the last task as not completed so that we
            // cover the case where all tasks are past due
            const isCompleted = isLast ? false : isPastDue || isFirst;

            return {
              ...step,
              isCompleted,
            };
          });

          return newSelectedCropSteps;
        });
      }
    },
    [todayStart],
  );
  const onChangeTrays = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTrays(e.target.value);
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setTraysValidation(msg),
      validateGT: 0,
      isRequired: true,
      validateInt: true,
    });
  }, []);
  const onTraysBlur = React.useCallback((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setTraysValidation(msg),
      validateGT: 0,
      isRequired: true,
      validateInt: true,
    });
  }, []);

  const onSubmit = React.useCallback(() => {
    void createPlanting({
      refetchQueries: [
        {
          query: DASHBOARD_QUERY,
          variables: {
            todayStart: todayStart.toString(),
            sevenDaysFromTodayStart: sevenDaysFromTodayStart.toString(),
          },
        },
        {
          query: DASHBOARD_OVERDUE_TASKS_QUERY,
          variables: {
            todayStart: todayStart.toString(),
            first: 25,
          },
        },
        { query: SURPLUSES_BUFFERS_QUERY, variables: { timeAfter: todayStart } },
      ],
      update: (cache, { data }) => {
        const tasksCreated = (data?.createPlanting?.tasks_created?.length ?? 0) > 0;
        cache.modify({
          fields: {
            harvests(_, { DELETE }) {
              return DELETE;
            },
            plantings(_, { DELETE }) {
              return DELETE;
            },
            task_groups(existingGroup, { DELETE }) {
              return tasksCreated ? DELETE : existingGroup;
            },
            tasks(existingTasks, { DELETE }) {
              return tasksCreated ? DELETE : existingTasks;
            },
            farm_report(_, { DELETE }) {
              return DELETE;
            },
          },
        });
      },
      variables: {
        planting_time: harvestTime == null ? 0 : utils.toNoonTime(harvestTime, timezone).unix(),
        planting_state: PLANTING_STATE.SURPLUS,
        planting_status: plantingStatus,
        // Hacky way to make sure error comes from server instead of
        // crashing UI just in case cropSelected is somehow undefined
        crop_id: cropIdToUse ?? -1,
        order_id: orderInfo?.orderId ?? null,
        planting_notes: notes,
        trays: traysNumber,
        crop_steps_completed: selectedCropSteps
          .filter(({ isCompleted }) => isCompleted)
          .map(({ cropStep: step }) => getNumericID(step.id)),
        planting_time_is_start_time: plantingTimeIsStartTime,
      },
      onCompleted: (dataIn) => {
        if (dataIn !== undefined && dataIn.createPlanting != null) {
          const newPlantingData = dataIn.createPlanting;
          enqueueSnackbar(`Created Planting ${getID(newPlantingData.planting_created.id)}`, {
            variant: 'success',
          });
          setHarvestTime(utils.todayStart(timezone));
          setCropIdToUse(null);
          setPlantingStatus(PLANTING_STATUS.PLANNED);
          setTrays('');
          setTraysValidation('');
          setNotes('');
          setSelectedCropSteps([]);
          setPlantingTimeIsStartTime(false);
          onSuccess(true);
        } else {
          enqueueSnackbar('Something went wrong when creating planting!', {
            variant: 'error',
            autoHideDuration: 10000,
          });
        }
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, {
          variant: 'error',
          autoHideDuration: 10000,
        });
        clientLogger.error({ title: 'new_planting_error', message: errorIn.message });
      },
    });
  }, [
    timezone,
    createPlanting,
    harvestTime,
    cropIdToUse,
    traysNumber,
    notes,
    plantingStatus,
    onSuccess,
    todayStart,
    sevenDaysFromTodayStart,
    selectedCropSteps,
    orderInfo,
    plantingTimeIsStartTime,
  ]);

  const hasInputValidationError = React.useMemo(
    () => traysValidation !== '' || harvestDateError !== null,
    [traysValidation, harvestDateError],
  );
  const requiredFieldsFilled: boolean = React.useMemo(
    () => harvestTime !== null && cropIdToUse !== null && traysNumber > 0,
    [harvestTime, cropIdToUse, traysNumber],
  );
  const isSubmitDisabled: boolean = React.useMemo(
    () => !requiredFieldsFilled || hasInputValidationError || disableFields,
    [requiredFieldsFilled, hasInputValidationError, disableFields],
  );
  const onEnter = React.useCallback(() => {
    if (!isSubmitDisabled) {
      onSubmit();
    }
  }, [isSubmitDisabled, onSubmit]);
  const maxDate = React.useMemo(() => {
    if (orderDate == null) {
      return undefined;
    }
    if (!plantingTimeIsStartTime) {
      return orderDate;
    }
    if (cropIdToUse == null) {
      return orderDate;
    }
    const cropData = cropDataMap.get(cropIdToUse);
    if (cropData == null) {
      return orderDate;
    }

    return orderDate.subtract(cropData.total_grow_time, 'second');
  }, [plantingTimeIsStartTime, orderDate, cropDataMap, cropIdToUse]);

  const form = React.useMemo(
    () => (
      <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={2}
        >
          <ToggleButtonGroup
            value={plantingTimeIsStartTime}
            exclusive
            size="medium"
            onChange={onChangePlantingTimeIsStartTime}
            aria-label="select-toggle-date"
            sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
          >
            <ToggleButton value={true} aria-label="entered-date-is-start-date">
              Start Date
            </ToggleButton>
            <ToggleButton value={false} aria-label="entered-date-is-harvest-date">
              Harvest Date
            </ToggleButton>
          </ToggleButtonGroup>
          <InternalDatePicker
            label={`${plantingTimeIsStartTime ? 'Start' : 'Harvest'} Date *`}
            value={harvestTime}
            onChange={onChangeHarvestTime}
            disabled={disableFields}
            maxDate={maxDate}
            onError={orderDate !== undefined ? (newError) => setHarvestDateError(newError) : undefined}
            textFieldProps={{
              helperText: harvestDateErrorMessage,
            }}
            sx={{
              width: '100%',
            }}
          />
        </Box>
        <FormControl fullWidth>
          <InputLabel id="crop-selector">Crop *</InputLabel>
          <Select
            aria-describedby="crop-selector"
            id="crop-selector"
            labelId="crop-selector"
            label="Crop *"
            value={cropIdToUse ?? ''}
            onChange={onChangeCropId}
            required={true}
            disabled={disableFields}
          >
            {filteredCropsData.map((crop) => {
              const cropLabel = `${crop.crop_name} (${formatDuration(crop.total_grow_time)})`;
              return (
                <MenuItem key={crop.id} value={getNumericID(crop.id)}>
                  {cropLabel}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <TextField
          aria-describedby="trays"
          id="trays"
          label="Trays"
          value={trays}
          numberMin={0}
          type="number"
          inputMode="numeric"
          onChange={onChangeTrays}
          onBlur={onTraysBlur}
          disabled={disableFields}
          required
          hasSubtext
          onEnter={onEnter}
          subtext={traysValidation}
          error={traysValidation !== ''}
        />
        <Box display="flex" flexDirection="column" sx={{ pl: '14px' }}>
          <FormControl required={true}>
            <FormLabel id="planting-status-group-label">Planting Status</FormLabel>
            <RadioGroup
              aria-labelledby="planting-status-group-label"
              value={plantingStatus}
              onChange={onChangePlantingStatus}
              row
            >
              <FormControlLabel value={PLANTING_STATUS.PLANNED} control={<Radio />} label="Planned" />
              <FormControlLabel value={PLANTING_STATUS.IN_PROGRESS} control={<Radio />} label="In Progress" />
              <FormControlLabel value={PLANTING_STATUS.COMPLETED} control={<Radio />} label="Completed" />
            </RadioGroup>
          </FormControl>
        </Box>
        <TextField
          aria-describedby="planting-notes"
          id="planting-notes"
          label="Notes"
          value={notes}
          onChange={onChangeNotes}
          onEnter={onEnter}
          disabled={disableFields}
        />
      </Box>
    ),
    [
      disableFields,
      onChangeNotes,
      onEnter,
      notes,
      plantingStatus,
      trays,
      traysValidation,
      onChangePlantingStatus,
      onChangeTrays,
      onChangeHarvestTime,
      harvestTime,
      cropIdToUse,
      onChangeCropId,
      filteredCropsData,
      onTraysBlur,
      orderDate,
      harvestDateErrorMessage,
      plantingTimeIsStartTime,
      onChangePlantingTimeIsStartTime,
      maxDate,
    ],
  );

  const taskDisplay = React.useMemo(() => {
    const showTrayDisplay = weightFromTrays !== undefined;
    const showTaskDisplay = harvestTime != null && selectedCropSteps.length > 0;
    const showSeedDisplay = seedForTrays !== undefined && seedForTrays.seedNeeded !== undefined;
    if (!showTrayDisplay && !showTaskDisplay && !showSeedDisplay) {
      return null;
    }

    return (
      <Box sx={{ marginLeft: 1, marginRight: 1, marginTop: 3 }}>
        {/* {selectedCropStartOrHarvestTime && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="h6">{`${plantingTimeIsStartTime ? 'Harvest' : 'Start'} Date: ${formatTime(selectedCropStartOrHarvestTime.unix(), { timezone })}`}</Typography>
          </Box>
        )} */}
        {showTrayDisplay ? (
          <Box sx={{ mb: 1 }}>
            <DialogInfoTitle>Weight Calculation</DialogInfoTitle>
            <Typography variant="body1">
              {`${traysNumber} trays x ${weightFromTrays.yield} ${shortenWeightUnit(weightFromTrays.unit)} per tray = ${weightFromTrays.weight} ${shortenWeightUnit(weightFromTrays.unit)}`}
            </Typography>
          </Box>
        ) : null}
        {showSeedDisplay && seedForTrays.seedNeeded !== undefined ? (
          <Box sx={{ mb: 1 }}>
            <DialogInfoTitle>Seed Calculation</DialogInfoTitle>
            <Typography variant="body1">
              {`Seed Needed: ${formatNumber(seedForTrays.seedNeeded, language)} ${seedForTrays.seedUnit}${seedForTrays.seedPerTray ? ` (${seedForTrays.seedPerTray}${seedForTrays.shortenedSeedUnit ?? ''} / tray)` : ''}`}
            </Typography>
          </Box>
        ) : null}
        {showTaskDisplay ? (
          <Box>
            <DialogInfoTitle>Tasks</DialogInfoTitle>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {plantingStatus === PLANTING_STATUS.IN_PROGRESS
                ? 'Select which tasks are completed:'
                : plantingStatus === PLANTING_STATUS.COMPLETED
                  ? 'All tasks will be marked as completed:'
                  : 'All tasks will be marked as planned:'}
            </Typography>
            {selectedCropSteps.map(({ cropStep: step, taskTime, isCompleted }) => {
              const taskName = step.name;
              const label = `${taskName} (${formatTime(taskTime, { timezone })})`;

              return (
                <Box
                  key={step.id}
                  display="flex"
                  flexDirection="row"
                  flexWrap="nowrap"
                  alignItems="center"
                  justifyContent="flex-start"
                >
                  <FormControlLabel
                    disabled={plantingStatus !== PLANTING_STATUS.IN_PROGRESS}
                    control={
                      <Checkbox
                        checked={isCompleted}
                        onChange={(e) => onChangeStepCompleted(e, getNumericID(step.id))}
                      />
                    }
                    label={label}
                  />
                </Box>
              );
            })}
          </Box>
        ) : null}
      </Box>
    );
  }, [
    selectedCropSteps,
    timezone,
    harvestTime,
    plantingStatus,
    onChangeStepCompleted,
    weightFromTrays,
    traysNumber,
    seedForTrays,
    language,
    // plantingTimeIsStartTime,
    // selectedCropStartOrHarvestTime,
  ]);

  const dialogContent = React.useMemo(
    () => (
      <>
        {form}
        {taskDisplay}
      </>
    ),
    [form, taskDisplay],
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {isForOrder
              ? `Add New Planting to Order ${orderInfo.orderId} (${formattedOrderDate})`
              : 'New Surplus Planting'}
          </Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>{dialogContent}</DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button
          sx={{ width: { xs: '100%', md: '50%' } }}
          variant="outlined"
          onClick={onClose}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onClose();
            }
          }}
          disabled={disableFields}
        >
          Cancel
        </Button>
        <Button
          id="submit-new-planting-button"
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isSubmitDisabled) {
              onSubmit();
            }
          }}
          type="submit"
          disabled={isSubmitDisabled}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
